import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/api.service';

interface Article {
    _id: string;
    title: string;
    content: string;
    fontSize: number;
    fontFamily: string;
    color: string;
    bold: boolean;
    italic: boolean;
    author: string;
    createdAt: string;
    isVisible?: boolean;
}

interface ArticleDraft {
    title: string;
    content: string;
}

interface ArticlesPageProps {
    isAdmin: boolean;
}

const initialDraft: ArticleDraft = {
    title: '',
    content: '',
};

const fontFamilies = ['Arial', 'Georgia', 'Tahoma', 'Times New Roman', 'Verdana'];
const fontSizes = [14, 16, 18, 20, 24, 28, 32, 36];
const textColors = [
    '#111827', '#374151', '#6b7280', '#dc2626', '#ea580c', '#ca8a04',
    '#16a34a', '#059669', '#0891b2', '#2563eb', '#4f46e5', '#9333ea',
    '#c026d3', '#db2777', '#ffffff', '#000000',
];
const legacyFontSizes = ['14px', '16px', '18px', '20px', '24px', '28px', '36px'];
const defaultArticleColor = '#1f2937';

const isSafeColor = (color: string) => {
    if (/^#[0-9a-f]{6}$/i.test(color)) return true;
    const rgb = color.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
    return Boolean(rgb && rgb.slice(1).every((channel) => Number(channel) <= 255));
};

const sanitizeArticleHtml = (html: string) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const allowedTags = new Set([
        'P', 'DIV', 'STRONG', 'B', 'EM', 'I', 'U', 'BR', 'UL', 'OL', 'LI', 'FONT',
        'H1', 'H2', 'H3', 'BLOCKQUOTE', 'SPAN',
    ]);
    const blockedTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'IMG', 'VIDEO', 'AUDIO']);
    const allowedFonts = new Set(fontFamilies);
    const cleanNode = (node: Node): Node | null => {
        if (node.nodeType === Node.TEXT_NODE) return parsed.createTextNode(node.textContent || '');
        if (node.nodeType !== Node.ELEMENT_NODE) return null;

        const source = node as HTMLElement;
        if (blockedTags.has(source.tagName)) return null;
        const children = Array.from(source.childNodes).map(cleanNode).filter((child): child is Node => child !== null);
        if (!allowedTags.has(source.tagName)) {
            const fragment = parsed.createDocumentFragment();
            children.forEach((child) => fragment.appendChild(child));
            return fragment;
        }

        const target = parsed.createElement(source.tagName === 'FONT' ? 'span' : source.tagName.toLowerCase());
        const requestedFontSize = source.style.fontSize
            || (source.tagName === 'FONT' ? legacyFontSizes[Number(source.getAttribute('size')) - 1] : '');
        const fontSize = requestedFontSize.match(/^(\d{1,2})px$/);
        if (fontSize && fontSizes.includes(Number(fontSize[1]))) target.style.fontSize = `${fontSize[1]}px`;
        const fontFamily = (source.style.fontFamily || (source.tagName === 'FONT' ? source.getAttribute('face') || '' : '')).replace(/['"]/g, '');
        if (allowedFonts.has(fontFamily)) target.style.fontFamily = fontFamily;
        const color = source.style.color || (source.tagName === 'FONT' ? source.getAttribute('color') || '' : '');
        if (isSafeColor(color)) target.style.color = color;
        if (['left', 'center', 'right', 'justify'].includes(source.style.textAlign)) {
            target.style.textAlign = source.style.textAlign;
        }
        if (source.style.fontWeight === 'bold' || Number(source.style.fontWeight) >= 600) target.style.fontWeight = '700';
        if (source.style.fontStyle === 'italic') target.style.fontStyle = 'italic';
        if (source.style.textDecoration.includes('underline')) target.style.textDecoration = 'underline';
        children.forEach((child) => target.appendChild(child));
        return target;
    };
    const container = parsed.createElement('div');
    Array.from(parsed.body.childNodes).map(cleanNode).filter((node): node is Node => node !== null)
        .forEach((node) => container.appendChild(node));
    return container.innerHTML;
};

const renderSafeArticleContent = (html: string): React.ReactNode => {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const allowedTags = new Set([
        'P', 'DIV', 'STRONG', 'B', 'EM', 'I', 'U', 'BR', 'UL', 'OL', 'LI', 'FONT',
        'H1', 'H2', 'H3', 'BLOCKQUOTE', 'SPAN',
    ]);
    const allowedFonts = new Set(fontFamilies);

    const renderNode = (node: Node, key: string): React.ReactNode => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent;
        if (node.nodeType !== Node.ELEMENT_NODE) return null;

        const element = node as HTMLElement;
        const children = Array.from(element.childNodes).map((child, index) => renderNode(child, `${key}-${index}`));
        if (!allowedTags.has(element.tagName)) return children;

        const style: React.CSSProperties = {};
        const requestedFontSize = element.style.fontSize
            || (element.tagName === 'FONT' ? legacyFontSizes[Number(element.getAttribute('size')) - 1] : '')
            || '';
        const fontSize = typeof requestedFontSize === 'string'
            ? requestedFontSize.match(/^(\d{1,2})px$/)
            : null;
        if (fontSize) {
            const size = Number(fontSize[1]);
            if (fontSizes.includes(size)) style.fontSize = `${size}px`;
        }
        const fontFamily = element.style.fontFamily || (element.tagName === 'FONT' ? element.getAttribute('face') || '' : '');
        if (allowedFonts.has(fontFamily.replace(/['"]/g, ''))) {
            style.fontFamily = fontFamily;
        }
        const color = element.style.color || (element.tagName === 'FONT' ? element.getAttribute('color') || '' : '');
        if (isSafeColor(color)) style.color = color;
        if (['left', 'center', 'right', 'justify'].includes(element.style.textAlign)) {
            style.textAlign = element.style.textAlign as React.CSSProperties['textAlign'];
        }
        if (element.style.fontWeight === 'bold' || Number(element.style.fontWeight) >= 600) style.fontWeight = 700;
        if (element.style.fontStyle === 'italic') style.fontStyle = 'italic';
        if (element.style.textDecoration.includes('underline')) style.textDecoration = 'underline';

        const tagName = element.tagName === 'FONT' ? 'span' : element.tagName.toLowerCase();
        return React.createElement(tagName, { key, style }, ...children);
    };

    return Array.from(document.body.childNodes).map((node, index) => renderNode(node, `article-${index}`));
};

const ArticlesPage: React.FC<ArticlesPageProps> = ({ isAdmin }) => {
    const [articles, setArticles] = useState<Article[]>([]);
    const [draft, setDraft] = useState<ArticleDraft>(initialDraft);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [updatingArticleId, setUpdatingArticleId] = useState<string | null>(null);
    const [editingArticleId, setEditingArticleId] = useState<string | null>(null);
    const [hasTextSelection, setHasTextSelection] = useState(false);
    const [isColorPaletteOpen, setIsColorPaletteOpen] = useState(false);
    const editorRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const editorSelectionRef = useRef<Range | null>(null);

    const loadArticles = async () => {
        setError('');
        try {
            setArticles(await apiService.get<Article[]>('/articles'));
        } catch {
            setError('Không tải được bài viết. Vui lòng thử lại.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadArticles();
    }, []);

    const saveArticle = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const content = editorRef.current?.innerHTML || '';
        if (!editorRef.current?.textContent?.trim()) {
            setError('Vui lòng nhập nội dung bài viết.');
            return;
        }
        if (content.length > 50000) {
            setError('Nội dung bài viết không được vượt quá 50.000 ký tự định dạng.');
            return;
        }
        setError('');
        setMessage('');
        setIsSaving(true);
        try {
            const articleData = {
                ...draft,
                title: draft.title.trim(),
                content,
            };
            const article = editingArticleId
                ? await apiService.patch<Article>(`/articles/${editingArticleId}`, articleData)
                : await apiService.post<Article>('/articles', articleData);
            setArticles((current) => editingArticleId
                ? current.map((item) => item._id === article._id ? article : item)
                : [article, ...current]);
            setEditingArticleId(null);
            setDraft(initialDraft);
            if (editorRef.current) editorRef.current.innerHTML = '';
            editorSelectionRef.current = null;
            setHasTextSelection(false);
            setIsColorPaletteOpen(false);
            setMessage(editingArticleId ? 'Đã cập nhật bài viết.' : 'Đã đăng bài viết cho người dùng.');
        } catch (requestError: any) {
            const responseMessage = requestError.response?.data?.message;
            setError(Array.isArray(responseMessage)
                ? responseMessage.join(', ')
                : responseMessage || (editingArticleId
                    ? 'Không thể cập nhật bài viết. Vui lòng thử lại.'
                    : 'Không thể đăng bài viết. Vui lòng thử lại.'));
        } finally {
            setIsSaving(false);
        }
    };

    const editArticle = (article: Article) => {
        setEditingArticleId(article._id);
        const content = sanitizeArticleHtml(article.content);
        setDraft({ title: article.title, content });
        if (editorRef.current) editorRef.current.innerHTML = content;
        editorSelectionRef.current = null;
        setHasTextSelection(false);
        setIsColorPaletteOpen(false);
        setError('');
        setMessage('');
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const cancelEditing = () => {
        setEditingArticleId(null);
        setDraft(initialDraft);
        if (editorRef.current) editorRef.current.innerHTML = '';
        editorSelectionRef.current = null;
        setHasTextSelection(false);
        setIsColorPaletteOpen(false);
        setError('');
        setMessage('');
    };

    const runEditorCommand = (command: string, value?: string) => {
        const selection = window.getSelection();
        const savedRange = editorSelectionRef.current;
        const editor = editorRef.current;
        const requiresTextSelection = ['fontSize', 'fontName', 'foreColor'].includes(command);
        const hasValidSavedRange = Boolean(savedRange && editor?.contains(savedRange.commonAncestorContainer));

        if (requiresTextSelection && (!savedRange || !hasValidSavedRange || savedRange.collapsed || !savedRange.toString())) {
            setHasTextSelection(false);
            return;
        }
        if (savedRange && hasValidSavedRange && selection) {
            selection.removeAllRanges();
            selection.addRange(savedRange);
        }
        editor?.focus();
        const fontSize = command === 'fontSize' ? Number(value) : null;
        const commandValue = fontSize ? String(Math.min(fontSizes.indexOf(fontSize) + 1, 7)) : value;
        document.execCommand(command, false, commandValue);
        if (fontSize && editorRef.current) {
            const fontCommandSize = commandValue;
            const selectedRange = window.getSelection()?.rangeCount
                ? window.getSelection()?.getRangeAt(0)
                : null;
            editorRef.current.querySelectorAll(`font[size="${fontCommandSize}"]`).forEach((font) => {
                if (!selectedRange?.intersectsNode(font)) return;
                const span = document.createElement('span');
                span.style.fontSize = `${fontSize}px`;
                span.innerHTML = font.innerHTML;
                font.replaceWith(span);
            });
        }
        if (command === 'fontName' || command === 'foreColor') {
            const selectedRange = window.getSelection()?.rangeCount
                ? window.getSelection()?.getRangeAt(0)
                : null;
            editorRef.current?.querySelectorAll('font').forEach((font) => {
                if (!selectedRange?.intersectsNode(font)) return;
                const span = document.createElement('span');
                const face = font.getAttribute('face')?.replace(/['"]/g, '');
                const color = font.getAttribute('color') || '';
                const legacySize = legacyFontSizes[Number(font.getAttribute('size')) - 1];
                if (face && fontFamilies.includes(face)) span.style.fontFamily = face;
                if (isSafeColor(color)) span.style.color = color;
                if (legacySize) span.style.fontSize = legacySize;
                span.innerHTML = font.innerHTML;
                font.replaceWith(span);
            });
        }
        const updatedSelection = window.getSelection();
        const updatedRange = updatedSelection?.rangeCount ? updatedSelection.getRangeAt(0) : null;
        if (updatedRange && editor?.contains(updatedRange.commonAncestorContainer) && !updatedRange.collapsed && updatedRange.toString()) {
            editorSelectionRef.current = updatedRange.cloneRange();
            setHasTextSelection(true);
        } else if (savedRange && editor?.contains(savedRange.commonAncestorContainer) && !savedRange.collapsed && savedRange.toString()) {
            editorSelectionRef.current = savedRange.cloneRange();
            if (updatedSelection) {
                updatedSelection.removeAllRanges();
                updatedSelection.addRange(savedRange);
            }
            setHasTextSelection(true);
        }
        if (editorRef.current) {
            setDraft((current) => ({ ...current, content: editorRef.current?.innerHTML || '' }));
        }
    };

    const saveEditorSelection = () => {
        const selection = window.getSelection();
        if (!selection?.rangeCount) return;
        const range = selection.getRangeAt(0);
        if (editorRef.current?.contains(range.commonAncestorContainer)) {
            editorSelectionRef.current = range.cloneRange();
            if (!range.collapsed && selection.toString().length > 0) {
                setHasTextSelection(true);
            } else {
                setHasTextSelection(false);
            }
        }
    };

    const setArticleVisibility = async (article: Article) => {
        const isVisible = article.isVisible === false;
        setUpdatingArticleId(article._id);
        setError('');
        setMessage('');
        try {
            const updated = await apiService.patch<Article>(`/articles/${article._id}/visibility`, { isVisible });
            setArticles((current) => current.map((item) => item._id === updated._id ? updated : item));
            setMessage(isVisible ? 'Đã hiện bài viết cho người dùng.' : 'Đã ẩn bài viết khỏi người dùng.');
        } catch (requestError: any) {
            const responseMessage = requestError.response?.data?.message;
            setError(Array.isArray(responseMessage)
                ? responseMessage.join(', ')
                : responseMessage || 'Không thể cập nhật trạng thái bài viết.');
        } finally {
            setUpdatingArticleId(null);
        }
    };

    const deleteArticle = async (article: Article) => {
        if (!window.confirm(`Xóa bài viết "${article.title}"? Thao tác này không thể hoàn tác.`)) return;
        setUpdatingArticleId(article._id);
        setError('');
        setMessage('');
        try {
            await apiService.delete(`/articles/${article._id}`);
            setArticles((current) => current.filter((item) => item._id !== article._id));
            if (editingArticleId === article._id) cancelEditing();
            setMessage('Đã xóa bài viết.');
        } catch (requestError: any) {
            const responseMessage = requestError.response?.data?.message;
            setError(Array.isArray(responseMessage)
                ? responseMessage.join(', ')
                : responseMessage || 'Không thể xóa bài viết.');
        } finally {
            setUpdatingArticleId(null);
        }
    };

    const formatDate = (value: string) => new Intl.DateTimeFormat('vi-VN', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));

    return (
        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
            <div className="mx-auto max-w-5xl space-y-6">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">Bài viết & thông báo</h2>
                    <p className="mt-1 text-sm text-gray-500">Các thông báo mới nhất dành cho người dùng.</p>
                </div>

                {isAdmin && (
                    <form ref={formRef} onSubmit={saveArticle} className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
                        <h3 className="font-semibold text-gray-900">{editingArticleId ? 'Chỉnh sửa bài viết' : 'Tạo bài viết'}</h3>
                        <label className="block text-sm font-medium text-gray-700">
                            Tiêu đề
                            <input
                                required
                                maxLength={160}
                                value={draft.title}
                                onChange={(event) => {
                                    const title = event.currentTarget.value;
                                    setDraft((current) => ({ ...current, title }));
                                }}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                                placeholder="Nhập tiêu đề thông báo"
                            />
                        </label>

                        <div>
                            <p className="mb-2 text-sm font-medium text-gray-700">Nội dung</p>
                            <p className="mb-2 text-xs text-gray-500">Bôi đen phần chữ cần định dạng, sau đó chọn cỡ chữ, font hoặc màu.</p>
                            <div
                                onMouseDown={(event) => {
                                    saveEditorSelection();
                                    if ((event.target as HTMLElement).closest('button')) event.preventDefault();
                                }}
                                className="mb-2 flex flex-wrap gap-2 rounded-t-lg border border-gray-300 bg-gray-50 p-2"
                            >
                                <button type="button" onClick={() => runEditorCommand('bold')} className="rounded border border-gray-300 px-3 py-1.5 font-bold hover:bg-white" title="In đậm">B</button>
                                <button type="button" onClick={() => runEditorCommand('italic')} className="rounded border border-gray-300 px-3 py-1.5 italic hover:bg-white" title="In nghiêng">I</button>
                                <button type="button" onClick={() => runEditorCommand('underline')} className="rounded border border-gray-300 px-3 py-1.5 underline hover:bg-white" title="Gạch chân">U</button>
                                <button type="button" onClick={() => runEditorCommand('formatBlock', '<h2>')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Tiêu đề">Tiêu đề</button>
                                <button type="button" onClick={() => runEditorCommand('formatBlock', '<p>')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Đoạn văn">Đoạn văn</button>
                                <select
                                    aria-label="Cỡ chữ nội dung"
                                    defaultValue=""
                                    onChange={(event) => {
                                        if (event.target.value) runEditorCommand('fontSize', String(fontSizes[Number(event.target.value) - 1]));
                                        event.target.value = '';
                                    }}
                                    disabled={!hasTextSelection}
                                    title={hasTextSelection ? 'Đổi cỡ chữ phần đã chọn' : 'Bôi đen chữ trước khi đổi cỡ'}
                                    className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <option value="" disabled>Cỡ chữ</option>
                                    {fontSizes.slice(0, 7).map((size, index) => <option key={size} value={index + 1}>{size}px</option>)}
                                </select>
                                <select
                                    aria-label="Font chữ nội dung"
                                    defaultValue=""
                                    onChange={(event) => {
                                        if (event.target.value) runEditorCommand('fontName', event.target.value);
                                        event.target.value = '';
                                    }}
                                    disabled={!hasTextSelection}
                                    title={hasTextSelection ? 'Đổi font chữ phần đã chọn' : 'Bôi đen chữ trước khi đổi font'}
                                    className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <option value="" disabled>Font chữ</option>
                                    {fontFamilies.map((font) => <option key={font} value={font}>{font}</option>)}
                                </select>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setIsColorPaletteOpen((open) => !open)}
                                        disabled={!hasTextSelection}
                                        title={hasTextSelection ? 'Chọn màu cho phần chữ đã bôi đen' : 'Bôi đen chữ trước khi đổi màu'}
                                        aria-label="Màu chữ trong nội dung"
                                        aria-expanded={isColorPaletteOpen}
                                        className="flex items-center gap-2 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Màu <span aria-hidden="true" className="h-4 w-4 rounded border border-gray-300" style={{ backgroundColor: defaultArticleColor }} />
                                    </button>
                                    {isColorPaletteOpen && hasTextSelection && (
                                        <div
                                            role="group"
                                            aria-label="Bảng chọn màu chữ"
                                            className="absolute left-0 top-full z-20 mt-1 grid w-40 grid-cols-4 gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
                                        >
                                            {textColors.map((color) => (
                                                <button
                                                    key={color}
                                                    type="button"
                                                    aria-label={`Chọn màu ${color}`}
                                                    title={color}
                                                    onClick={() => runEditorCommand('foreColor', color)}
                                                    className="h-7 w-7 rounded border border-gray-300 outline-offset-2 hover:scale-110 focus:outline focus:outline-2 focus:outline-blue-500"
                                                    style={{ backgroundColor: color }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button type="button" onClick={() => runEditorCommand('insertUnorderedList')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Danh sách dấu đầu dòng">• Danh sách</button>
                                <button type="button" onClick={() => runEditorCommand('insertOrderedList')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Danh sách đánh số">1. Danh sách</button>
                                <button type="button" onClick={() => runEditorCommand('justifyLeft')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Căn trái">≡ Trái</button>
                                <button type="button" onClick={() => runEditorCommand('justifyCenter')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Căn giữa">☰ Giữa</button>
                                <button type="button" onClick={() => runEditorCommand('justifyRight')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Căn phải">≡ Phải</button>
                                <button type="button" onClick={() => runEditorCommand('undo')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Hoàn tác">↶</button>
                                <button type="button" onClick={() => runEditorCommand('redo')} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" title="Làm lại">↷</button>
                            </div>
                            <div
                                ref={editorRef}
                                contentEditable
                                suppressContentEditableWarning
                                role="textbox"
                                aria-label="Nội dung bài viết"
                                aria-multiline="true"
                                onInput={(event) => {
                                    const content = event.currentTarget.innerHTML;
                                    setDraft((current) => ({ ...current, content }));
                                }}
                                onBlur={saveEditorSelection}
                                onKeyUp={saveEditorSelection}
                                onMouseUp={saveEditorSelection}
                                className="min-h-48 rounded-b-lg border border-t-0 border-gray-300 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-blue-100"
                                style={{ fontFamily: 'Arial', fontSize: '16px', color: defaultArticleColor }}
                                data-placeholder="Nhập nội dung bài viết..."
                            />
                        </div>
                        <div>
                            <p className="mb-2 text-xs font-medium text-gray-500">Xem trước</p>
                            <div className="min-h-16 whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-gray-50 p-3" style={{
                                color: defaultArticleColor,
                                fontSize: '16px',
                                fontFamily: 'Arial',
                            }}>
                                {draft.content ? renderSafeArticleContent(draft.content) : 'Nội dung bài viết sẽ hiển thị ở đây.'}
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isSaving
                                ? (editingArticleId ? 'Đang lưu...' : 'Đang đăng...')
                                : (editingArticleId ? 'Lưu thay đổi' : 'Đăng bài viết')}
                        </button>
                        {editingArticleId && (
                            <button
                                type="button"
                                onClick={cancelEditing}
                                disabled={isSaving}
                                className="ml-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Hủy chỉnh sửa
                            </button>
                        )}
                    </form>
                )}

                {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
                {message && <p role="status" className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{message}</p>}
                {isLoading ? (
                    <p className="py-8 text-center text-sm text-gray-500">Đang tải bài viết...</p>
                ) : articles.length === 0 ? (
                    <p className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">Chưa có bài viết nào.</p>
                ) : (
                    <div className="space-y-4">
                        {articles.map((article) => (
                            <article key={article._id} className={`rounded-xl border bg-white p-4 shadow-sm md:p-6 ${article.isVisible === false ? 'border-amber-300 opacity-75' : 'border-gray-200'}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">{article.title}</h3>
                                        <p className="mt-1 text-xs text-gray-500">
                                            {formatDate(article.createdAt)} · {article.author}
                                        </p>
                                    </div>
                                    {isAdmin && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            {article.isVisible === false && (
                                                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">Đang ẩn</span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => editArticle(article)}
                                                disabled={updatingArticleId === article._id || isSaving}
                                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                Sửa
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => void setArticleVisibility(article)}
                                                disabled={updatingArticleId === article._id}
                                                className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                                            >
                                                {updatingArticleId === article._id
                                                    ? 'Đang xử lý...'
                                                    : article.isVisible === false ? 'Hiện bài viết' : 'Ẩn bài viết'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => void deleteArticle(article)}
                                                disabled={updatingArticleId === article._id}
                                                className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                            >
                                                Xóa
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-4 whitespace-pre-wrap break-words" style={{
                                    color: article.color || defaultArticleColor,
                                    fontSize: `${article.fontSize || 16}px`,
                                    fontFamily: article.fontFamily || 'Arial',
                                }}>
                                    {renderSafeArticleContent(article.content)}
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArticlesPage;
